/* eslint-disable no-param-reassign, no-plusplus */
import { ToastNotification } from '@deliveroo/tools-component-library';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Debug from 'debug';

import mergeDiff from './merge';
import styles from './styles.module.scss';

const debug = Debug('rsa:ToastManager');
const CLOSE_TIMEOUT = 3000;
const TRANSITION_MS = 300;

/**
 * Get the item id from the DOM node.
 *
 * @param {HTMLElement} el
 */
const getNodeItemId = (el) => el.dataset.id;

/**
 * Takes care of adding and removing toasts when the redux
 * `notifications` state changes.
 *
 * This component transitions items in and out which adds
 * significant complexity The component needs to store
 * it's own local `renderItems` list that represents items
 * that are currently rendered.
 *
 * When an item is removed from the list it must stay in the
 * local `renderItems` list until the transition is complete.
 *
 * This is perhaps a little over-engineered for the requirements
 * right now, but if we ever need to show more than one notification
 * at a time, this component will be ready for it 💪
 *
 */
class ToastManager extends Component {
  static propTypes = {
    removeNotification: PropTypes.func.isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        subtitle: PropTypes.string,
      }).isRequired,
    ),
  };

  static defaultProps = {
    items: [],
  };

  constructor(props) {
    super(props);
    const renderItems = mergeDiff([], props.items);
    this.state = { renderItems };
    this.el = React.createRef();
  }

  /**
   * Called after a render has taken place as a due
   * to a change within `this.props` or `this.state`.
   *
   * When a snapshot is provided (from `getSnapshotBeforeUpdate()`)
   * it means that changed `renderItems` triggered the update and
   * we should initiate the transition between states.
   *
   * @param {*} prevProps
   * @param {*} prevState
   * @param {*} snapshot the result of `getSnapshotBeforeUpdate()`
   */
  componentDidUpdate(prevProps, prevState, snapshot) {
    if (snapshot) {
      debug('got snapshot, transitioning …');
      this.transitionItems(snapshot).then(this.cleanRemovedItems);
      return;
    }

    const { renderItems } = this.state;
    const prevItems = prevProps.items;
    const nextItems = this.props.items;

    debug('component did update', renderItems.length);

    if (nextItems === prevItems) {
      debug('abort: items unchanged', prevItems, nextItems);
      return;
    }

    const nextRenderItems = mergeDiff(prevItems, nextItems);

    // TODO: if transitioning postpone this
    if (nextRenderItems.changed) {
      this.setState({ renderItems: nextRenderItems }); // eslint-disable-line react/no-did-update-set-state
    }
  }

  /**
   * When a notification is closed, either automatically or manually
   * we call `removeNotification()` which removes it from the redux store.
   *
   * @param {number} id
   */
  onClose = (id) => {
    debug('on close', id);
    this.props.removeNotification(id);
  };

  /**
   * Get all the current item nodes in the DOM.
   *
   * @returns {Element[]}
   */
  getItemNodes() {
    return Array.from(this.el.current.querySelectorAll('[data-item]'));
  }

  /**
   * This hook is called before react makes a changes to the DOM.
   * We use it to record the y-position of the items already
   * in the DOM, so that after `render()` we can measure
   * them again and work out the distance they should
   * be transitioned.
   *
   * https://reactjs.org/docs/react-component.html#getsnapshotbeforeupdate
   *
   * @param {*} prevProps
   * @param {*} prevState
   */
  getSnapshotBeforeUpdate(prevProps, prevState) {
    debug('get snapshot before update');
    const renderItemsChanged = this.state.renderItems !== prevState.renderItems;

    if (!renderItemsChanged) {
      debug('abort: renderItems unchanged');
      return null;
    }

    if (!this.el.current) {
      debug('abort: no root el yet');
      return null;
    }

    // returns a truthy result even when there are no items
    return this.getItemNodes().reduce((result, el) => {
      const id = getNodeItemId(el);
      result[id] = el.getBoundingClientRect().top;
      return result;
    }, {});
  }

  /**
   * Transitions the items in the DOM during the `getSnapShotBeforeUpdate`
   * callback from there old position to their new.
   *
   * @param snapshot the y-positions of the item nodes *before* the update
   * @returns {Promise}
   */
  transitionItems(snapshot) {
    return new Promise((resolve) => {
      debug('transition');
      const { states } = this.state.renderItems;
      let count = 0;

      this.getItemNodes().forEach((el) => {
        const id = getNodeItemId(el);
        const yStart = snapshot[id];
        const isEntering = !yStart;
        const isLeaving = states[id] === 'remove';

        const yEnd = el.getBoundingClientRect().top;
        const deltaY = !isEntering ? yStart - yEnd : 100;

        const transform = `translateY(${deltaY}px)`;
        let opacity;

        if (isEntering) {
          debug('is new!!');
          opacity = '0';
        }

        const positionChanged = transform !== el.style.transform;

        // OPTIMIZATION: don't transition this item if it didn't change position
        if (!positionChanged) {
          debug('abort: no style changes');
          return;
        }

        el.style.transition = '';
        el.style.transform = transform;
        el.style.opacity = opacity;

        requestAnimationFrame(() => {
          debug('trigger animation', deltaY);
          el.style.transition = `transform ${TRANSITION_MS}ms, opacity ${TRANSITION_MS}ms`;
          el.style.transform = 'translateY(0px)';
          el.style.opacity = isLeaving ? 0 : 1;
          count++;

          el.addEventListener('transitionend', function fn() {
            el.removeEventListener('transitionend', fn);
            // resolve when all the transitions have ended
            if (!--count) resolve();
          });
        });
      });
    });
  }

  /**
   * Removes all the render items that are marked as 'remove'.
   * This should be called after 'out' transitions have ended
   * and they are no longer visible.
   */
  cleanRemovedItems = () => {
    debug('clean removed items');

    const {
      renderItems: { items, states },
    } = this.state;

    const nextItems = items.filter(({ id }) => states[id] !== 'remove');

    if (!nextItems.length === items.length) {
      debug('abort: nothing to clean');
      return;
    }

    // triggers a render *without* the removed items
    // so only visible items exist in the DOM
    this.setState({
      renderItems: {
        items: nextItems,
        states: {},
      },
    });
  };

  /**
   * Renders the 'render items' into their natural/post-transition
   * positions. `getSnapshotBeforeUpdate()` is run before this
   * so that we can measure the positions of items *before*
   * they we updated and transition them into place when
   * `componentDidUpdate()` is called.
   */
  render() {
    const { renderItems } = this.state;
    debug('render items: %s', renderItems.items.length);

    return (
      <div className={styles.toastManager} ref={this.el}>
        {renderItems.items.map(
          ({ title, subtitle, id, autoCloseTimeoutMs }) => {
            const wasRemoved = renderItems.states[id] === 'remove';

            // collapse removed items so that siblings fall into the position they would
            // be in if this item didn't exist allowing us to read their end position
            const height = wasRemoved ? 0 : undefined;

            return (
              <div key={id} data-item={id} style={{ height }}>
                <ToastNotification
                  spacing="0 medium medium 0"
                  id={id}
                  title={title}
                  subtitle={subtitle}
                  onClose={this.onClose}
                  autoCloseTimeoutMs={
                    autoCloseTimeoutMs === null
                      ? null
                      : autoCloseTimeoutMs || CLOSE_TIMEOUT
                  }
                />
              </div>
            );
          },
        )}
      </div>
    );
  }
}

export default ToastManager;
